import { View } from "react-native";
import {
  MAX_CYCLE_WEEKS,
  MAX_SESSIONS,
  type IntensityGrid,
} from "@jackedlogs/shared";

import {
  IntensityGridView,
  type GridPlacement,
} from "../IntensityGrid/IntensityGridView";
import type { ConfirmFn } from "../seams";
import type { ComposerStyles } from "./composerStyles";
import { CloneDayBar } from "./CloneDayBar";
import { CloneWeekBar } from "./CloneWeekBar";
import type { CloneDayResult, CloneWeekUiResult } from "./useComposer";

// The program's day×week navigation spine, rendered above the exercise list
// in the edit-in-place shell. It carries what the day list can't: the ONE
// day×week grid (PE-27 — the whole program's shape, the always-visible cursor
// AND the open exercise's placement, all in one object), and the ⧉ clone-day /
// clone-week composing actions.
//
// ⛔ Controls that used to live here and are gone, all absorbed by the grid:
// the day-selector CHIP STRIP (the grid is the cursor), the week STEPPER, the
// remove-day button, and — since PE-27 — the "Intensity grid ▸" DISCLOSURE.
// The last one went by arithmetic rather than taste: once the heat and the
// cursor are the same object, folding it away would fold away navigation.
// Absorbing an additive control is a removal; absorbing a destructive one
// without replacing it would have been a capability hole, which is why the
// first pass kept the stepper.
// ⚠️ The stepper's − was not even the same gesture: setCycleWeeks truncates
// the TAIL, while the grid's − drops the week you point at (removeWeekAt).
export function ProgramSpine({
  grid,
  selectedDay,
  onSelectDay,
  selectedWeek,
  onSelectCell,
  placement,
  onRemoveWeek,
  dark,
  dayCount,
  weekCount,
  hasContent,
  resolveName,
  cloneDay,
  cloneWeek,
  addDay,
  removeDay,
  changeCycleWeeks,
  confirm,
  cs,
}: {
  grid: IntensityGrid;
  selectedDay: number;
  onSelectDay: (day: number) => void;
  selectedWeek: number;
  onSelectCell: (dayIdx: number, week: number) => void;
  /** null while no exercise is open — the matrix is then a cursor only. */
  placement: GridPlacement | null;
  onRemoveWeek?: (week: number) => void;
  dark: boolean;
  dayCount: number;
  weekCount: number;
  hasContent: boolean;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  cloneDay: (toDay: number) => CloneDayResult;
  cloneWeek: (fromWeek: number, toWeeks: number[]) => CloneWeekUiResult;
  // PE-11 — structural edits. Add a training day, remove one (guarded at 1),
  // resize the cycle. Absent → the controls hide (create-time / single-plan).
  addDay?: (name: string) => void;
  removeDay?: (idx: number) => void;
  changeCycleWeeks?: (weeks: number) => void;
  /** PE-29 — the platform confirm behind hold-to-remove (week / day). */
  confirm?: ConfirmFn;
  cs: ComposerStyles;
}) {
  return (
    <View style={{ gap: 10 }}>
      {/* PE-27 — ONE grid, and it is always on screen. It used to be two:
          a collapsible read-only heat grid plus an always-visible placement
          matrix, transposed against each other. Fusing them ends the
          disclosure by arithmetic, not by taste — the thing it hid (the heat)
          is now the same object as the thing that must stay (the day/week
          cursor, which decides WHICH exercises the list below shows). Hiding
          it would hide navigation.
          ⚠️ Deliberately a rewire and not a redesign (Antenore 2026-08-28):
          the classic composer is transitional — it dies when `prescribe` is
          promoted to stable — so it gets the fused grid with the props it
          already passed, and the cure for DS-63 that comes with it, and no
          new design work. */}
      <IntensityGridView
        grid={grid}
        selected={selectedDay}
        onSelect={onSelectDay}
        selectedWeek={selectedWeek}
        onSelectCell={onSelectCell}
        placement={placement}
        dark={dark}
        title={null}
        onRemoveWeek={changeCycleWeeks ? onRemoveWeek : undefined}
        // PE-29 — without it the grid offers no removal at all: hold-to-remove
        // replaced the armed − buttons, and a hold with no dialog behind it
        // would drop a week on a mis-touch.
        confirm={confirm}
        onAddWeek={
          changeCycleWeeks && weekCount < MAX_CYCLE_WEEKS
            ? () => changeCycleWeeks(weekCount + 1)
            : undefined
        }
        onAddDay={
          addDay && dayCount < MAX_SESSIONS
            ? () => {
                addDay(`D${dayCount + 1}`);
                onSelectDay(dayCount);
              }
            : undefined
        }
        onRemoveDay={
          removeDay
            ? (dayIdx) => {
                removeDay(dayIdx);
                onSelectDay(Math.max(0, dayIdx - 1));
              }
            : undefined
        }
      />

      <CloneDayBar
        dayCount={dayCount}
        selectedDay={selectedDay}
        hasContent={hasContent}
        resolveName={resolveName}
        onClone={cloneDay}
        styles={cs}
      />
      <CloneWeekBar
        weekCount={weekCount}
        resolveName={resolveName}
        onClone={cloneWeek}
        styles={cs}
      />
    </View>
  );
}
